import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  TrendingUp,
  RotateCcw,
  Zap,
  CheckSquare,
  ArrowRight,
  ChevronRight,
  ShieldCheck,
  CheckCircle2,
  EyeOff,
  ClipboardList,
  Network,
  Archive,
  Radio,
  Monitor,
  Gauge,
  Code2,
  Sparkles,
  GitFork,
  Database,
  ArrowLeftRight,
  Layers,
  Users,
  Factory,
  Warehouse,
  Wrench,
  Code,
  Cpu,
  BarChart2,
  Wifi,
  LineChart,
  LayoutDashboard,
  Bell,
  HardHat,
  Briefcase,
  Truck,
  CheckCheck,
  TimerReset,
  TimerOff,
  Crosshair,
  Quote,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { openAgent } from "../utils/openAgent";

const SkipperCaseStudy = () => {
  const navigate = useNavigate();
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Sequential wave animation: lifts cards one by one from Skipper Limited to Digitization, then repeats
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCardIndex((prev) => (prev + 1) % 4);
    }, 1800);
    return () => clearInterval(interval);
  }, []);

  const handleDiscoverClick = () => {
    openAgent();
  };

  const statMetrics = [
    {
      id: "stat-1",
      icon: TrendingUp,
      value: "30%",
      label: "Increase in production efficiency",
    },
    {
      id: "stat-2",
      icon: RotateCcw,
      value: "25%",
      label: "Reduction in downtime",
    },
    {
      id: "stat-3",
      icon: Zap,
      value: "18%",
      label: "Faster decision making",
    },
    {
      id: "stat-4",
      icon: CheckSquare,
      value: "Multiple",
      label: "Facilities across locations",
    },
  ];

  const pillTags = [
    {
      label: "Manufacturing",
      iconSrc: "/images/icons/icon-manufacturing.png",
    },
    {
      label: "Operations",
      iconSrc: "/images/icons/icon-operations.png",
    },
    {
      label: "Process Digitization",
      iconSrc: "/images/icons/icon-process-digitization.png",
    },
  ];

  const overviewCards = [
    {
      id: "client",
      iconSrc: "/images/icons/overview-client.png",
      label: "CLIENT",
      value: "Skipper Limited",
      activeBorder: "#0284C7",
      defaultBorder: "rgba(2, 132, 199, 0.2)",
      activeShadow: "0 18px 32px -6px rgba(2, 132, 199, 0.26), 0 6px 14px -4px rgba(15, 23, 42, 0.08)",
    },
    {
      id: "industry",
      iconSrc: "/images/icons/overview-industry.png",
      label: "INDUSTRY",
      value: "Manufacturing",
      activeBorder: "#A855F7",
      defaultBorder: "rgba(168, 85, 247, 0.2)",
      activeShadow: "0 18px 32px -6px rgba(168, 85, 247, 0.26), 0 6px 14px -4px rgba(15, 23, 42, 0.08)",
    },
    {
      id: "focus",
      iconSrc: "/images/icons/overview-focus.png",
      label: "FOCUS AREA",
      value: "Operations",
      activeBorder: "#EA580C",
      defaultBorder: "rgba(234, 88, 12, 0.2)",
      activeShadow: "0 18px 32px -6px rgba(234, 88, 12, 0.26), 0 6px 14px -4px rgba(15, 23, 42, 0.08)",
    },
    {
      id: "solution",
      iconSrc: "/images/icons/overview-solution.png",
      label: "SOLUTION TYPE",
      value: "Digitization",
      activeBorder: "#22C55E",
      defaultBorder: "rgba(34, 197, 94, 0.2)",
      activeShadow: "0 18px 32px -6px rgba(34, 197, 94, 0.26), 0 6px 14px -4px rgba(15, 23, 42, 0.08)",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="min-h-screen w-full bg-[#010710] text-white flex flex-col justify-between selection:bg-[#00D2EE]/30 selection:text-white"
    >
      <Navbar />

      {/* TOP HERO SECTION MATCHING SCREENSHOT */}
      <section className="relative w-full pt-[100px] sm:pt-[120px] pb-16 lg:pb-24 overflow-hidden">
        {/* Ambient Top-Left Blue Glow */}
        <div
          className="pointer-events-none absolute left-0 top-0 w-[640px] h-[360px] opacity-75 blur-[96px]"
          style={{
            background:
              "radial-gradient(ellipse at 15% 10%, rgba(23, 56, 98, 0.95) 0%, rgba(23, 56, 98, 0.4) 55%, transparent 80%)",
          }}
        />

        {/* Ambient Center Glow */}
        <div
          className="pointer-events-none absolute right-1/4 top-1/3 w-[500px] h-[300px] opacity-35 blur-[120px]"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(0, 180, 216, 0.4) 0%, transparent 70%)",
          }}
        />

        <div className="relative mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
          {/* Main 2-Column Header (Left: Details & Pills, Right: Framed Photo) */}
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12 xl:gap-16">
            {/* Left Column */}
            <div className="flex flex-col gap-6 lg:col-span-6 z-10">
              {/* Breadcrumb Navigation */}
              <div className="flex items-center flex-wrap gap-2 text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[2px] text-white/60">
                <Link
                  to="/cognitive"
                  className="hover:text-[#00D2EE] transition-colors"
                >
                  COGNITIVE AI
                </Link>
                <span className="text-white/40">&gt;</span>
                <Link
                  to="/case-studies"
                  className="hover:text-[#00D2EE] transition-colors"
                >
                  CASE STUDIES
                </Link>
                <span className="text-white/40">&gt;</span>
                <span className="text-white/90">SKIPPER LTD</span>
              </div>

              {/* Main Heading */}
              <h1 className="text-[34px] sm:text-[44px] md:text-[50px] lg:text-[54px] font-bold leading-[1.12] tracking-tight text-white">
                Skipper Limited
              </h1>

              {/* Subheading */}
              <h2 className="text-[18px] sm:text-[21px] md:text-[23px] font-medium leading-[1.3] text-white/95">
                Digitizing manufacturing operations for greater efficiency.
              </h2>

              {/* Paragraph Description */}
              <p className="max-w-[540px] text-[14.5px] sm:text-[15.5px] leading-relaxed text-[#94A3B8]">
                How Skipper Limited partnered with Simpel to streamline
                manufacturing processes across galvanizing and tower operations
                with digital transformation.
              </p>

              {/* 3 Pill Badges (Matching screenshot) */}
              <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 pt-2">
                {pillTags.map((pill) => (
                  <div
                    key={pill.label}
                    className="inline-flex items-center gap-2.5 rounded-full border border-[#0284C7]/50 bg-[#021627]/90 px-4 sm:px-4.5 py-1.5 sm:py-2 text-[12.5px] sm:text-[13px] font-medium text-[#38BDF8] shadow-[0_2px_12px_rgba(2,132,199,0.15)] select-none"
                  >
                    <img
                      src={pill.iconSrc}
                      alt={pill.label}
                      className="h-4 w-4 object-contain select-none pointer-events-none"
                    />
                    <span>{pill.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column: Framed Team Photo (No border outline, completely static with no hover movement) */}
            <div className="flex w-full items-center justify-center lg:col-span-6 lg:justify-end z-10">
              <div className="relative w-full max-w-[620px] rounded-[22px] sm:rounded-[28px] shadow-[0_20px_50px_rgba(0,0,0,0.65)] overflow-hidden bg-[#020B16]">
                <img
                  src="/images/skipper-team.png"
                  alt="Skipper Limited Team at Industrial Exhibition"
                  className="w-full h-auto aspect-[831/509] object-cover block select-none pointer-events-none"
                />
              </div>
            </div>
          </div>

          {/* 4 Bottom Stat Metrics Cards Grid (Matching screenshot, completely static with no hover) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mt-12 sm:mt-16 z-10">
            {statMetrics.map((stat) => {
              const Icon = stat.icon;
              return (
                <div
                  key={stat.id}
                  className="flex items-center gap-4 rounded-[14px] sm:rounded-[16px] border border-[#062942]/60 bg-[#021825] p-5 sm:p-6 shadow-[0_4px_20px_rgba(0,0,0,0.25)] select-none"
                >
                  {/* Icon Box */}
                  <div className="flex h-11 w-11 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-[10px] bg-[#133951] text-[#5bb8fe]">
                    <Icon className="h-5 w-5 sm:h-[22px] sm:w-[22px] text-[#5bb8fe]" strokeWidth={2.2} />
                  </div>

                  {/* Stat Text Column */}
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="text-[28px] sm:text-[32px] font-bold leading-none text-white tracking-tight">
                      {stat.value}
                    </span>
                    <span className="text-[12px] sm:text-[12.5px] leading-tight text-[#94A3B8]">
                      {stat.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 01 PROJECT OVERVIEW SECTION (Matching Screenshot) */}
      <section className="relative w-full py-16 sm:py-20 lg:py-24 bg-white text-[#0B132B]">
        <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
          <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12 xl:gap-16">
            {/* Left Column: Number Badge, Title, and Description */}
            <div className="flex flex-col gap-5 lg:col-span-5">
              {/* Header with 01 Badge and Title */}
              <div className="flex items-center gap-3.5">
                <span className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-full bg-[#E0F2FE] text-[12.5px] sm:text-[13.5px] font-bold text-[#0284C7] shadow-sm">
                  01
                </span>
                <h2 className="text-[26px] sm:text-[30px] lg:text-[34px] font-bold tracking-tight text-[#0B132B]">
                  Project Overview
                </h2>
              </div>

              {/* Description Paragraph */}
              <p className="max-w-[460px] text-[14.5px] sm:text-[15.5px] leading-[25px] sm:leading-[27px] text-[#64748B]">
                Skipper Limited is one of the leading manufacturers in transmission and distribution structures, with diverse operations across galvanizing and tower manufacturing.
              </p>
            </div>

            {/* Right Column: 4 Overview Cards with Sequential Wave Lift Animation */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 sm:gap-4 lg:gap-4 xl:gap-5 lg:col-span-7 pt-3.5 sm:pt-4">
              {overviewCards.map((card, idx) => {
                const isActive = idx === activeCardIndex;
                return (
                  <div
                    key={card.id}
                    className="flex flex-col items-center justify-center rounded-[18px] bg-white p-5 sm:p-6 text-center select-none cursor-default"
                    style={{
                      border: `1.5px solid ${isActive ? card.activeBorder : card.defaultBorder}`,
                      transform: isActive ? "translateY(-12px)" : "translateY(0px)",
                      boxShadow: isActive
                        ? card.activeShadow
                        : "0 2px 10px rgba(15, 23, 42, 0.04)",
                      transition:
                        "transform 0.55s cubic-bezier(0.34, 1.3, 0.64, 1), box-shadow 0.55s ease, border-color 0.4s ease",
                      willChange: "transform, box-shadow, border-color",
                    }}
                  >
                    <div className="mb-3 h-11 w-11 sm:h-12 sm:w-12">
                      <img
                        src={card.iconSrc}
                        alt={card.label}
                        className="h-full w-full object-contain pointer-events-none"
                      />
                    </div>
                    <span className="text-[10px] sm:text-[10.5px] font-bold uppercase tracking-[1.5px] text-[#64748B]">
                      {card.label}
                    </span>
                    <span className="mt-1 text-[13.5px] sm:text-[14.5px] font-bold text-[#0B132B] whitespace-nowrap">
                      {card.value}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 02 THE CHALLENGE SECTION (Matching Screenshot) */}
      <section className="relative w-full py-16 sm:py-20 lg:py-24 bg-[#F0F7FD] text-[#0B132B]">
        <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
          <div className="grid grid-cols-1 items-stretch gap-8 lg:grid-cols-12 lg:gap-8 xl:gap-10">
            {/* Left Column: 02 Badge, Title, Description & Callout Box */}
            <div className="flex flex-col justify-between gap-6 lg:col-span-4">
              <div className="flex flex-col gap-4">
                {/* Header with 02 Badge and Title */}
                <div className="flex items-center gap-3.5">
                  <span className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-full bg-[#E0F2FE] text-[12.5px] sm:text-[13.5px] font-bold text-[#0284C7] shadow-sm">
                    02
                  </span>
                  <h2 className="text-[26px] sm:text-[30px] lg:text-[34px] font-bold tracking-tight text-[#0B132B]">
                    The Challenge
                  </h2>
                </div>

                {/* Description Paragraph */}
                <p className="text-[14.5px] sm:text-[15px] leading-[25px] sm:leading-[26px] text-[#475569]">
                  Skipper Limited faced operational complexities due to multiple facilities, manual processes, and disconnected systems. Real-time visibility across production, inventory, and quality was limited, making it difficult to optimize resources and respond quickly to changing demand.
                </p>
              </div>

              {/* Callout Card (Bottom of Left Column) */}
              <div className="flex items-start gap-3.5 rounded-[18px] bg-[#BED7F8] p-5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center text-[#0B132B] pt-0.5">
                  <Radio className="h-6 w-6 text-[#0B132B]" strokeWidth={2.2} />
                </div>
                <p className="text-[13px] sm:text-[13.5px] font-medium leading-[21px] sm:leading-[22px] text-[#0B132B]">
                  Lagging production feedback resulted in unpredictable dispatch schedules across cross-state supply chains.
                </p>
              </div>
            </div>

            {/* Middle Column: Control Room Image */}
            <div className="relative flex flex-col justify-center lg:col-span-4">
              <div className="relative h-full min-h-[340px] sm:min-h-[380px] w-full overflow-hidden rounded-[20px] shadow-[0_4px_20px_rgba(15,23,42,0.08)] bg-slate-200">
                <img
                  src="/images/control-room-baseline.jpg"
                  alt="Control Room Baseline"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />
                <div className="absolute bottom-4 left-5 right-5 pointer-events-none">
                  <span className="text-[12.5px] sm:text-[13px] font-medium text-white/95 tracking-wide">
                    Control Room Baseline • 2024
                  </span>
                </div>
              </div>
            </div>

            {/* Right Column: 4 Challenge Items */}
            <div className="flex flex-col justify-between gap-3.5 sm:gap-4 lg:col-span-4">
              {/* Item 1 */}
              <div className="flex items-center gap-4 rounded-[18px] bg-white p-4 sm:p-5 shadow-[0_2px_12px_rgba(15,23,42,0.04)] border border-slate-100">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[#E0F2FE] text-[#0284C7]">
                  <EyeOff className="h-5 w-5" strokeWidth={2} />
                </div>
                <span className="text-[13.5px] sm:text-[14.5px] font-bold leading-snug text-[#0B132B]">
                  Limited real-time visibility across operations
                </span>
              </div>

              {/* Item 2 */}
              <div className="flex items-center gap-4 rounded-[18px] bg-white p-4 sm:p-5 shadow-[0_2px_12px_rgba(15,23,42,0.04)] border border-slate-100">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[#E0F2FE] text-[#0284C7]">
                  <ClipboardList className="h-5 w-5" strokeWidth={2} />
                </div>
                <span className="text-[13.5px] sm:text-[14.5px] font-bold leading-snug text-[#0B132B]">
                  Manual processes leading to delays and errors
                </span>
              </div>

              {/* Item 3 */}
              <div className="flex items-center gap-4 rounded-[18px] bg-white p-4 sm:p-5 shadow-[0_2px_12px_rgba(15,23,42,0.04)] border border-slate-100">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[#E0F2FE] text-[#0284C7]">
                  <Network className="h-5 w-5" strokeWidth={2} />
                </div>
                <span className="text-[13.5px] sm:text-[14.5px] font-bold leading-snug text-[#0B132B]">
                  Fragmented systems across multiple facilities
                </span>
              </div>

              {/* Item 4 */}
              <div className="flex items-center gap-4 rounded-[18px] bg-white p-4 sm:p-5 shadow-[0_2px_12px_rgba(15,23,42,0.04)] border border-slate-100">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[#E0F2FE] text-[#0284C7]">
                  <Archive className="h-5 w-5" strokeWidth={2} />
                </div>
                <span className="text-[13.5px] sm:text-[14.5px] font-bold leading-snug text-[#0B132B]">
                  Difficulty tracking production and inventory in real time
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 03 THE SOLUTION SECTION (Matching Screenshot) */}
      <section className="relative w-full py-16 sm:py-20 lg:py-24 bg-white text-[#0B132B]">
        <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
          {/* Top Header Row: 03 Badge, Title, and Right Paragraph */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-12 lg:mb-14">
            <div className="flex items-center gap-3.5">
              <span className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-full bg-[#E0F2FE] text-[12.5px] sm:text-[13.5px] font-bold text-[#0284C7] shadow-sm">
                03
              </span>
              <h2 className="text-[26px] sm:text-[30px] lg:text-[34px] font-bold tracking-tight text-[#0B132B]">
                The Solution
              </h2>
            </div>
            <p className="max-w-[560px] text-[14px] sm:text-[14.5px] leading-[23px] sm:leading-[24px] text-[#64748B]">
              Simpel partnered with Skipper Limited to design and implement a connected, digital manufacturing solution that streamlined operations, improved visibility, and enabled data-driven decision making.
            </p>
          </div>

          {/* Top Row: 4 Solution Pillars / Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 mb-10 lg:mb-12">
            {/* Pillar 1: Process Digitization */}
            <div className="flex flex-col justify-between rounded-[20px] border border-slate-200/80 bg-white p-6 shadow-[0_2px_12px_rgba(15,23,42,0.03)]">
              <div>
                <div className="mb-4 flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-[14px] bg-[#E0F2FE] text-[#0284C7]">
                  <Monitor className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.2} />
                </div>
                <h3 className="mb-2 text-[16px] sm:text-[17px] font-bold text-[#0B132B]">
                  Process Digitization
                </h3>
                <p className="text-[13px] sm:text-[13.5px] leading-relaxed text-[#64748B]">
                  Digitized key manufacturing workflows across galvanizing and tower operations.
                </p>
              </div>
            </div>

            {/* Pillar 2: Real-time Monitoring */}
            <div className="flex flex-col justify-between rounded-[20px] border border-slate-200/80 bg-white p-6 shadow-[0_2px_12px_rgba(15,23,42,0.03)]">
              <div>
                <div className="mb-4 flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-[14px] bg-[#DCFCE7] text-[#16A34A]">
                  <Gauge className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.2} />
                </div>
                <h3 className="mb-2 text-[16px] sm:text-[17px] font-bold text-[#0B132B]">
                  Real-time Monitoring
                </h3>
                <p className="text-[13px] sm:text-[13.5px] leading-relaxed text-[#64748B]">
                  Implemented real-time dashboards for production, quality, and inventory monitoring.
                </p>
              </div>
            </div>

            {/* Pillar 3: System Integration */}
            <div className="flex flex-col justify-between rounded-[20px] border border-slate-200/80 bg-white p-6 shadow-[0_2px_12px_rgba(15,23,42,0.03)]">
              <div>
                <div className="mb-4 flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-[14px] bg-[#FFEDD5] text-[#EA580C]">
                  <Code2 className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.2} />
                </div>
                <h3 className="mb-2 text-[16px] sm:text-[17px] font-bold text-[#0B132B]">
                  System Integration
                </h3>
                <p className="text-[13px] sm:text-[13.5px] leading-relaxed text-[#64748B]">
                  Integrated multiple systems and data sources for a unified view across facilities.
                </p>
              </div>
            </div>

            {/* Pillar 4: Data-driven Insights */}
            <div className="flex flex-col justify-between rounded-[20px] border border-slate-200/80 bg-white p-6 shadow-[0_2px_12px_rgba(15,23,42,0.03)]">
              <div>
                <div className="mb-4 flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-[14px] bg-[#FCE7F3] text-[#DB2777]">
                  <Sparkles className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.2} />
                </div>
                <h3 className="mb-2 text-[16px] sm:text-[17px] font-bold text-[#0B132B]">
                  Data-driven Insights
                </h3>
                <p className="text-[13px] sm:text-[13.5px] leading-relaxed text-[#64748B]">
                  Enabled advanced analytics for better forecasting and operational decisions.
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Container: Solution Architecture */}
          <div className="rounded-[24px] bg-[#F0F7FD] p-6 sm:p-8 lg:p-10 border border-slate-200/60 shadow-[0_2px_16px_rgba(15,23,42,0.03)]">
            {/* Architecture Header */}
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <div className="flex items-center gap-2.5">
                <GitFork className="h-5 w-5 sm:h-[22px] sm:w-[22px] text-[#0284C7]" strokeWidth={2.2} />
                <h3 className="text-[18px] sm:text-[20px] font-bold text-[#0B132B]">
                  Solution Architecture
                </h3>
              </div>
              <span className="text-[12px] sm:text-[13px] font-medium text-[#64748B]">
                End-to-End Orchestration
              </span>
            </div>

            {/* 4 Architecture Columns Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {/* Column 1: Data Sources */}
              <div className="flex flex-col rounded-[18px] bg-white p-4 sm:p-5 border border-slate-100 shadow-sm">
                <div className="flex items-center justify-between pb-3.5 mb-3.5 border-b border-slate-100">
                  <span className="text-[14.5px] sm:text-[15px] font-bold text-[#0B132B]">
                    Data Sources
                  </span>
                  <Database className="h-4 w-4 text-[#0284C7]" strokeWidth={2.2} />
                </div>
                <div className="flex flex-col gap-2.5">
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-[#F1F5F9] px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-semibold text-[#1E293B]">
                    <Factory className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>Production Systems</span>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-[#F1F5F9] px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-semibold text-[#1E293B]">
                    <CheckCircle2 className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>Quality Systems</span>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-[#F1F5F9] px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-semibold text-[#1E293B]">
                    <Warehouse className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>Inventory & Warehouse</span>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-[#F1F5F9] px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-semibold text-[#1E293B]">
                    <Wrench className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>Maintenance Systems</span>
                  </div>
                </div>
              </div>

              {/* Column 2: Integration Layer */}
              <div className="flex flex-col rounded-[18px] bg-white p-4 sm:p-5 border border-slate-100 shadow-sm">
                <div className="flex items-center justify-between pb-3.5 mb-3.5 border-b border-slate-100">
                  <span className="text-[14.5px] sm:text-[15px] font-bold text-[#0B132B]">
                    Integration Layer
                  </span>
                  <ArrowLeftRight className="h-4 w-4 text-[#0284C7]" strokeWidth={2.2} />
                </div>
                <div className="flex flex-col gap-2.5">
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-[#F1F5F9] px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-semibold text-[#1E293B]">
                    <Code className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>APIs & Connectors</span>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-[#F1F5F9] px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-semibold text-[#1E293B]">
                    <Cpu className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>Data Processing</span>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-[#F1F5F9] px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-semibold text-[#1E293B]">
                    <BarChart2 className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>Real-time Analytics</span>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-[#F1F5F9] px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-semibold text-[#1E293B]">
                    <Wifi className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>Edge Sync</span>
                  </div>
                </div>
              </div>

              {/* Column 3: Simpel Platform (Highlighted Blue Card) */}
              <div className="flex flex-col rounded-[18px] bg-[#BED7F8] p-4 sm:p-5 border-2 border-[#93C5FD] shadow-md">
                <div className="flex items-center justify-between pb-3.5 mb-3.5 border-b border-[#93C5FD]/60">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-[#0284C7] shadow-sm animate-pulse" />
                    <span className="text-[14.5px] sm:text-[15px] font-bold text-[#0B132B]">
                      Simpel Platform
                    </span>
                  </div>
                  <Layers className="h-4 w-4 text-[#0284C7]" strokeWidth={2.2} />
                </div>
                <div className="flex flex-col gap-2.5">
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-white px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-bold text-[#0B132B] shadow-sm">
                    <Sparkles className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>Process Optimization</span>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-white px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-bold text-[#0B132B] shadow-sm">
                    <LineChart className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>Analytics & Insights</span>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-white px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-bold text-[#0B132B] shadow-sm">
                    <LayoutDashboard className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>Dashboards & Reporting</span>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-white px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-bold text-[#0B132B] shadow-sm">
                    <Bell className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>Alerts & Notifications</span>
                  </div>
                </div>
              </div>

              {/* Column 4: Users & Stakeholders */}
              <div className="flex flex-col rounded-[18px] bg-white p-4 sm:p-5 border border-slate-100 shadow-sm">
                <div className="flex items-center justify-between pb-3.5 mb-3.5 border-b border-slate-100">
                  <span className="text-[14.5px] sm:text-[15px] font-bold text-[#0B132B]">
                    Users & Stakeholders
                  </span>
                  <Users className="h-4 w-4 text-[#0284C7]" strokeWidth={2.2} />
                </div>
                <div className="flex flex-col gap-2.5">
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-[#F1F5F9] px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-semibold text-[#1E293B]">
                    <HardHat className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>Plant Operations</span>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-[#F1F5F9] px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-semibold text-[#1E293B]">
                    <Briefcase className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>Management</span>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-[#F1F5F9] px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-semibold text-[#1E293B]">
                    <Truck className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>Supply Chain</span>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-[12px] bg-[#F1F5F9] px-3.5 py-2.5 text-[12.5px] sm:text-[13px] font-semibold text-[#1E293B]">
                    <CheckCheck className="h-4 w-4 text-[#0284C7] shrink-0" strokeWidth={2} />
                    <span>Quality & Maintenance</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 04 THE IMPACT SECTION (Matching Screenshot) */}
      <section className="relative w-full py-16 sm:py-20 lg:py-24 bg-[#010814] text-white overflow-hidden">
        {/* Background Wallpaper Image with Opacity */}
        <img
          src="/images/impact-bg.png"
          alt="Impact Wave Background"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-40 mix-blend-screen"
        />
        {/* Soft Vignette Overlay to Blend Smoothly */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#010814]/80 via-transparent to-[#010814]/80" />

        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
          {/* Header Row: 04 Badge, Title, and Description in One Single Line */}
          <div className="flex flex-col gap-2.5 w-full">
            <div className="flex items-center gap-3.5">
              <span className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-full bg-white text-[12.5px] sm:text-[13.5px] font-bold text-[#0B132B] shadow-sm">
                04
              </span>
              <h2 className="text-[28px] sm:text-[32px] lg:text-[36px] font-bold tracking-tight text-white">
                The Impact
              </h2>
            </div>
            <p className="text-[13.5px] sm:text-[14px] lg:text-[14.5px] xl:text-[15px] leading-relaxed text-[#94A3B8] whitespace-normal xl:whitespace-nowrap w-full">
              The digital transformation helped Skipper Limited improve operational efficiency, gain real-time visibility, and make faster, data-driven decisions.
            </p>
          </div>

          {/* 4 Impact Metric Cards (Exact Figma Spec + Top 4px Accent Line) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 mt-10 lg:mt-12">
            {/* Card 1: 30% Production Efficiency (Line: rgba(91, 184, 254, 1), Height: 4px) */}
            <div
              className="relative flex flex-col justify-start w-full overflow-hidden"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)",
                borderRadius: "12px",
                padding: "32px 32px 52px 32px",
                gap: "6px",
                backdropFilter: "blur(40px)",
                WebkitBackdropFilter: "blur(40px)",
                boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
              }}
            >
              {/* Top Line: width 310px / 100%, height 4px */}
              <div
                className="absolute top-0 left-0 right-0 h-[4px] w-full"
                style={{ background: "rgba(91, 184, 254, 1)" }}
              />

              <div className="flex items-center justify-between w-full">
                <TrendingUp className="h-[22px] w-[22px] text-[#00A3FF]" strokeWidth={2.4} />
                <span className="rounded-[6px] bg-white px-2.5 py-1 text-[11px] font-bold text-[#0284C7] shadow-sm tracking-wide">
                  +30%
                </span>
              </div>
              <div className="text-[32px] sm:text-[36px] font-bold tracking-tight text-[#00A3FF] leading-none">
                30%
              </div>
              <p className="text-[13px] sm:text-[13.5px] leading-[20px] text-[#CBD5E1]">
                Increase in production efficiency across galvanizing lines.
              </p>
            </div>

            {/* Card 2: 25% Reduction in Downtime (Line: rgba(0, 99, 152, 1), Height: 4px) */}
            <div
              className="relative flex flex-col justify-start w-full overflow-hidden"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)",
                borderRadius: "12px",
                padding: "32px 32px 52px 32px",
                gap: "6px",
                backdropFilter: "blur(40px)",
                WebkitBackdropFilter: "blur(40px)",
                boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
              }}
            >
              {/* Top Line: width 310px / 100%, height 4px */}
              <div
                className="absolute top-0 left-0 right-0 h-[4px] w-full"
                style={{ background: "rgba(0, 99, 152, 1)" }}
              />

              <div className="flex items-center justify-between w-full">
                <TimerOff className="h-[22px] w-[22px] text-[#00A3FF]" strokeWidth={2.4} />
                <span className="rounded-[6px] bg-white px-2.5 py-1 text-[11px] font-bold text-[#0284C7] shadow-sm tracking-wide">
                  -25%
                </span>
              </div>
              <div className="text-[32px] sm:text-[36px] font-bold tracking-tight text-[#00A3FF] leading-none">
                25%
              </div>
              <p className="text-[13px] sm:text-[13.5px] leading-[20px] text-[#CBD5E1]">
                Reduction in unplanned machine downtime and maintenance delays.
              </p>
            </div>

            {/* Card 3: 18% Faster Decision Making (Line: rgba(91, 184, 254, 1), Height: 4px) */}
            <div
              className="relative flex flex-col justify-start w-full overflow-hidden"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)",
                borderRadius: "12px",
                padding: "32px 32px 52px 32px",
                gap: "6px",
                backdropFilter: "blur(40px)",
                WebkitBackdropFilter: "blur(40px)",
                boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
              }}
            >
              {/* Top Line: width 310px / 100%, height 4px */}
              <div
                className="absolute top-0 left-0 right-0 h-[4px] w-full"
                style={{ background: "rgba(91, 184, 254, 1)" }}
              />

              <div className="flex items-center justify-between w-full">
                <Gauge className="h-[22px] w-[22px] text-[#00A3FF]" strokeWidth={2.4} />
                <span className="rounded-[6px] bg-white px-2.5 py-1 text-[11px] font-bold text-[#0284C7] shadow-sm tracking-wide">
                  Faster
                </span>
              </div>
              <div className="text-[32px] sm:text-[36px] font-bold tracking-tight text-[#00A3FF] leading-none">
                18%
              </div>
              <p className="text-[13px] sm:text-[13.5px] leading-[20px] text-[#CBD5E1]">
                Faster operational decision making from integrated floor analytics.
              </p>
            </div>

            {/* Card 4: Multiple Facilities (Line: rgba(0, 99, 152, 1), Height: 4px) */}
            <div
              className="relative flex flex-col justify-start w-full overflow-hidden"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)",
                borderRadius: "12px",
                padding: "32px 32px 52px 32px",
                gap: "6px",
                backdropFilter: "blur(40px)",
                WebkitBackdropFilter: "blur(40px)",
                boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
              }}
            >
              {/* Top Line: width 310px / 100%, height 4px */}
              <div
                className="absolute top-0 left-0 right-0 h-[4px] w-full"
                style={{ background: "rgba(0, 99, 152, 1)" }}
              />

              <div className="flex items-center justify-between w-full">
                <svg
                  className="h-[22px] w-[22px] text-[#00A3FF]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="9" strokeDasharray="3 3.5" />
                  <circle cx="12" cy="12" r="2.2" fill="currentColor" />
                  <path d="M12 6.5v1.5" />
                </svg>
                <span className="rounded-[6px] bg-white px-2.5 py-1 text-[11px] font-bold text-[#0284C7] shadow-sm tracking-wide">
                  Scale
                </span>
              </div>
              <div className="text-[32px] sm:text-[36px] font-bold tracking-tight text-[#00A3FF] leading-none">
                Multiple
              </div>
              <p className="text-[13px] sm:text-[13.5px] leading-[20px] text-[#CBD5E1]">
                Production facilities successfully synchronized pan-India.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 05 CLIENT PERSPECTIVE SECTION (Matching Screenshot) */}
      <section className="relative w-full py-16 sm:py-20 lg:py-24 bg-gradient-to-r from-[#DFECF7] via-[#D6E6F4] to-[#CCE0F1] text-[#0B132B] overflow-hidden">
        {/* Ambient Wave Texture */}
        <img
          src="/images/impact-bg.png"
          alt="Ambient Waves"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.07] mix-blend-multiply"
        />

        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
          {/* Header Row: 05 Badge, Title, and Description */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8 lg:mb-10">
            <div className="flex items-center gap-3.5">
              <span className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-full bg-[#E0F2FE] text-[12.5px] sm:text-[13.5px] font-bold text-[#0284C7] shadow-sm">
                05
              </span>
              <h2 className="text-[26px] sm:text-[30px] lg:text-[34px] font-bold tracking-tight text-[#0B132B]">
                Client Perspective
              </h2>
            </div>
            <div className="lg:border-l lg:border-slate-300/80 lg:pl-6 max-w-[500px]">
              <p className="text-[13px] sm:text-[13.5px] leading-[22px] text-[#475569]">
                Skipper Limited's team experienced significant improvements in operational efficiency, visibility and decision making with digital solution.
              </p>
            </div>
          </div>

          {/* Quote Card (Matching Screenshot) */}
          <div className="rounded-[20px] bg-white p-7 sm:p-9 lg:p-11 shadow-[0_6px_28px_rgba(15,23,42,0.06)] border border-white/80">
            <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8">
              {/* Left Quote Icon Box */}
              <div className="flex h-12 w-12 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-[14px] bg-[#006398] text-white shadow-sm">
                <Quote className="h-6 w-6 text-white fill-white" />
              </div>

              {/* Right Quote Content */}
              <div className="flex flex-col justify-between w-full">
                <blockquote className="text-[16px] sm:text-[18px] lg:text-[19px] font-medium italic leading-[28px] sm:leading-[32px] text-[#1E293B]">
                  “The digital solution implemented by Simpel has helped us streamline our manufacturing operations and gain real-time visibility across multiple facilities. It has significantly improved our efficiency and decision making.”
                </blockquote>

                {/* Author & Badge Row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-slate-100">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[14px] sm:text-[15px] font-bold text-[#0B132B]">
                      Operations Team
                    </span>
                    <span className="text-[12.5px] sm:text-[13px] text-[#64748B]">
                      Skipper Limited
                    </span>
                  </div>

                  {/* Skipper Limited Pill Badge Image */}
                  <img
                    src="/images/skipper-limited-badge.png"
                    alt="Skipper Limited Badge"
                    className="h-11 sm:h-12 lg:h-[54px] w-auto object-contain self-start sm:self-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 06 SOLUTION IN ACTION SECTION */}
      <section
        className="relative w-full py-16 sm:py-20 lg:py-24 text-[#0B132B]"
        style={{ backgroundColor: "rgba(242, 249, 254, 1)" }}
      >
        <div className="relative mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
          {/* Header Row: Title and Subtitle */}
          <div className="flex flex-col gap-2 max-w-[700px]">
            <h2 className="text-[28px] sm:text-[32px] lg:text-[36px] font-bold tracking-tight text-[#0B132B]">
              Solution in Action
            </h2>
            <p className="text-[14px] sm:text-[15px] leading-relaxed text-[#64748B]">
              Intuitive touchpoints connecting physical manufacturing lines with high-velocity telemetry.
            </p>
          </div>

          {/* 3 Cards Grid (Exact Figma Spec) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-10 lg:mt-12">
            {/* Card 1: Real-time Production Dashboard */}
            <div
              className="flex flex-col justify-start w-full max-w-[418px] mx-auto overflow-hidden p-3.5 sm:p-4"
              style={{
                backgroundColor: "rgba(255, 255, 255, 1)",
                borderRadius: "12px",
                border: "4px solid rgba(248, 248, 248, 1)",
                boxShadow:
                  "0px 4px 20px 0px rgba(0, 0, 0, 0.1), inset 0px -5px 20px 0px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div className="w-full overflow-hidden rounded-[10px] bg-slate-100 shadow-sm">
                <img
                  src="/images/solution-dashboard.jpg"
                  alt="Real-time Production Dashboard"
                  className="h-[192px] w-full max-w-[418px] object-cover"
                  style={{ height: "192px" }}
                />
              </div>
              <div className="pt-4 px-1 pb-1">
                <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0B132B]">
                  Real-time Production Dashboard
                </h3>
                <p className="mt-1.5 text-[12.5px] sm:text-[13px] leading-[20px] text-[#64748B]">
                  Monitor production, quality and inventory across multiple facilities with sub-second sync.
                </p>
              </div>
            </div>

            {/* Card 2: Connected Operations */}
            <div
              className="flex flex-col justify-start w-full max-w-[418px] mx-auto overflow-hidden p-3.5 sm:p-4"
              style={{
                backgroundColor: "rgba(255, 255, 255, 1)",
                borderRadius: "12px",
                border: "4px solid rgba(248, 248, 248, 1)",
                boxShadow:
                  "0px 4px 20px 0px rgba(0, 0, 0, 0.1), inset 0px -5px 20px 0px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div className="w-full overflow-hidden rounded-[10px] bg-slate-100 shadow-sm">
                <img
                  src="/images/solution-connected.jpg"
                  alt="Connected Operations"
                  className="h-[192px] w-full max-w-[418px] object-cover"
                  style={{ height: "192px" }}
                />
              </div>
              <div className="pt-4 px-1 pb-1">
                <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0B132B]">
                  Connected Operations
                </h3>
                <p className="mt-1.5 text-[12.5px] sm:text-[13px] leading-[20px] text-[#64748B]">
                  Digitize and streamline workflows across galvanizing baths and tower structural assembly lines.
                </p>
              </div>
            </div>

            {/* Card 3: Data-driven Insights */}
            <div
              className="flex flex-col justify-start w-full max-w-[418px] mx-auto overflow-hidden p-3.5 sm:p-4"
              style={{
                backgroundColor: "rgba(255, 255, 255, 1)",
                borderRadius: "12px",
                border: "4px solid rgba(248, 248, 248, 1)",
                boxShadow:
                  "0px 4px 20px 0px rgba(0, 0, 0, 0.1), inset 0px -5px 20px 0px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div className="w-full overflow-hidden rounded-[10px] bg-slate-100 shadow-sm">
                <img
                  src="/images/solution-insights.jpg"
                  alt="Data-driven Insights"
                  className="h-[192px] w-full max-w-[418px] object-cover"
                  style={{ height: "192px" }}
                />
              </div>
              <div className="pt-4 px-1 pb-1">
                <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0B132B]">
                  Data-driven Insights
                </h3>
                <p className="mt-1.5 text-[12.5px] sm:text-[13px] leading-[20px] text-[#64748B]">
                  Gain actionable insights for automated demand forecasting and optimized resource allocation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 07 RELATED CASE STUDIES SECTION */}
      <section
        className="relative w-full py-16 sm:py-20 lg:py-24 text-[#0B132B]"
        style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
      >
        <div className="relative mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
          {/* Header Row: EXPLORE MORE / Related Case Studies + View All Link */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 pb-10 sm:pb-12">
            <div className="flex flex-col gap-2">
              <span className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2px] text-[#94A3B8]">
                EXPLORE MORE
              </span>
              <h2 className="text-[28px] sm:text-[32px] lg:text-[36px] font-bold tracking-tight text-[#0B132B]">
                Related Case Studies
              </h2>
            </div>

            <Link
              to="/case-studies"
              className="group inline-flex items-center gap-1.5 self-start sm:self-auto font-semibold text-[15px] sm:text-[16px] leading-[24px] text-[#0284C7] hover:text-[#0369A1] transition-colors"
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              <span>View All Case Studies</span>
              <span className="transition-transform duration-300 group-hover:translate-x-1">&rarr;</span>
            </Link>
          </div>

          {/* 3 Related Case Study Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Card 1: Aptia UK */}
            <div
              className="group relative flex flex-col justify-between overflow-hidden rounded-[18px] border border-slate-200/90 bg-white transition-all duration-300 hover:border-[#BAE6FD] hover:shadow-[0_12px_32px_rgba(37,99,235,0.08)]"
              style={{
                boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.05)",
              }}
            >
              {/* Hover Gradient Overlay */}
              <div
                className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background:
                    "linear-gradient(145deg, #ffffff 15%, rgba(220, 248, 252, 0.75) 55%, rgba(198, 222, 253, 0.92) 100%)",
                }}
              />

              <div className="relative z-10">
                <div className="w-full overflow-hidden bg-slate-100">
                  <img
                    src="/images/related-aptia-clean.jpg"
                    alt="Aptia UK"
                    className="h-[210px] w-full object-cover"
                  />
                </div>
                <div className="p-6 sm:p-7 pb-2">
                  <span className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[1.5px] text-[#64748B]">
                    FINANCIAL SERVICES | GROWTH
                  </span>
                  <h3 className="text-[19px] sm:text-[20px] font-bold text-[#0B132B] mt-2 mb-2 tracking-tight">
                    Aptia UK
                  </h3>
                  <p className="text-[13px] sm:text-[13.5px] leading-[22px] text-[#64748B]">
                    Driving customer acquisition and improving digital user journeys across global pension services.
                  </p>
                </div>
              </div>

              <div className="relative z-10 p-6 sm:p-7 pt-4">
                <Link
                  to="/case-studies"
                  className="inline-flex items-center gap-1.5 font-semibold text-[16px] leading-[24px]"
                  style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: "16px",
                    lineHeight: "24px",
                    letterSpacing: "0px",
                    background:
                      "linear-gradient(90deg, rgba(5, 214, 218, 1) 0%, rgba(49, 112, 247, 1) 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  <span>Read Case Study</span>
                  <span className="transition-transform duration-300 group-hover:translate-x-1.5">&rarr;</span>
                </Link>
              </div>
            </div>

            {/* Card 2: Global Logistics Provider */}
            <div
              className="group relative flex flex-col justify-between overflow-hidden rounded-[18px] border border-slate-200/90 bg-white transition-all duration-300 hover:border-[#BAE6FD] hover:shadow-[0_12px_32px_rgba(37,99,235,0.08)]"
              style={{
                boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.05)",
              }}
            >
              {/* Hover Gradient Overlay */}
              <div
                className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background:
                    "linear-gradient(145deg, #ffffff 15%, rgba(220, 248, 252, 0.75) 55%, rgba(198, 222, 253, 0.92) 100%)",
                }}
              />

              <div className="relative z-10">
                <div className="w-full overflow-hidden bg-slate-100">
                  <img
                    src="/images/related-logistics.jpg"
                    alt="Global Logistics Provider"
                    className="h-[210px] w-full object-cover"
                  />
                </div>
                <div className="p-6 sm:p-7 pb-2">
                  <span className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[1.5px] text-[#64748B]">
                    SUPPLY CHAIN | OPERATIONS
                  </span>
                  <h3 className="text-[19px] sm:text-[20px] font-bold text-[#0B132B] mt-2 mb-2 tracking-tight">
                    Global Logistics Provider
                  </h3>
                  <p className="text-[13px] sm:text-[13.5px] leading-[22px] text-[#64748B]">
                    End-to-end visibility and demand forecasting for a more resilient international supply chain.
                  </p>
                </div>
              </div>

              <div className="relative z-10 p-6 sm:p-7 pt-4">
                <Link
                  to="/case-studies"
                  className="inline-flex items-center gap-1.5 font-semibold text-[16px] leading-[24px]"
                  style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: "16px",
                    lineHeight: "24px",
                    letterSpacing: "0px",
                    background:
                      "linear-gradient(90deg, rgba(5, 214, 218, 1) 0%, rgba(49, 112, 247, 1) 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  <span>Read Case Study</span>
                  <span className="transition-transform duration-300 group-hover:translate-x-1.5">&rarr;</span>
                </Link>
              </div>
            </div>

            {/* Card 3: Healthcare Network */}
            <div
              className="group relative flex flex-col justify-between overflow-hidden rounded-[18px] border border-slate-200/90 bg-white transition-all duration-300 hover:border-[#BAE6FD] hover:shadow-[0_12px_32px_rgba(37,99,235,0.08)]"
              style={{
                boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.05)",
              }}
            >
              {/* Hover Gradient Overlay */}
              <div
                className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  background:
                    "linear-gradient(145deg, #ffffff 15%, rgba(220, 248, 252, 0.75) 55%, rgba(198, 222, 253, 0.92) 100%)",
                }}
              />

              <div className="relative z-10">
                <div className="w-full overflow-hidden bg-slate-100">
                  <img
                    src="/images/related-healthcare.jpg"
                    alt="Healthcare Network"
                    className="h-[210px] w-full object-cover"
                  />
                </div>
                <div className="p-6 sm:p-7 pb-2">
                  <span className="text-[11px] sm:text-[11.5px] font-bold uppercase tracking-[1.5px] text-[#64748B]">
                    HEALTHCARE | OPERATIONS
                  </span>
                  <h3 className="text-[19px] sm:text-[20px] font-bold text-[#0B132B] mt-2 mb-2 tracking-tight">
                    Healthcare Network
                  </h3>
                  <p className="text-[13px] sm:text-[13.5px] leading-[22px] text-[#64748B]">
                    Streamlining clinical workflows and improving patient experience with digital transformation.
                  </p>
                </div>
              </div>

              <div className="relative z-10 p-6 sm:p-7 pt-4">
                <Link
                  to="/case-studies"
                  className="inline-flex items-center gap-1.5 font-semibold text-[16px] leading-[24px]"
                  style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: "16px",
                    lineHeight: "24px",
                    letterSpacing: "0px",
                    background:
                      "linear-gradient(90deg, rgba(5, 214, 218, 1) 0%, rgba(49, 112, 247, 1) 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  <span>Read Case Study</span>
                  <span className="transition-transform duration-300 group-hover:translate-x-1.5">&rarr;</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 08 A MORE INTELLIGENT TOMORROW / CTA SECTION */}
      <section
        id="skipper-cta"
        className="relative flex min-h-[420px] sm:min-h-[460px] lg:min-h-[500px] w-full items-center overflow-hidden bg-[#020B16]"
      >
        <img
          src="/images/intelligent-tomorrow-bg.png"
          alt="A More Intelligent Tomorrow"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover object-right sm:object-center"
        />

        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 py-16 sm:py-20 lg:px-[70px] lg:py-24">
          <div className="flex w-full max-w-[900px] flex-col items-start gap-4 sm:gap-5">
            <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-white/80">
              A MORE INTELLIGENT TOMORROW
            </p>
            <h2 className="text-[28px] sm:text-[36px] lg:text-[42px] font-bold leading-[1.15] tracking-tight text-white whitespace-nowrap">
              Let’s build what’s next. Together.
            </h2>
            <p className="text-[13px] sm:text-[14px] lg:text-[15.5px] leading-relaxed text-[#CBD5E1] whitespace-nowrap">
              Talk to our team to explore how technology and AI can create meaningful impact for your business.
            </p>
            <div className="pt-2 sm:pt-3">
              <button
                onClick={handleDiscoverClick}
                className="group flex items-center justify-center rounded-full border border-white/70 bg-[#04111F]/70 px-6 py-3 sm:px-7 sm:py-3.5 text-[14px] sm:text-[14.5px] font-medium text-white backdrop-blur-md transition-all duration-300 hover:border-white hover:bg-[#04111F]/90 hover:shadow-[0_0_20px_rgba(255,255,255,0.15)] active:scale-95 cursor-pointer"
              >
                <span>Talk to an AI Strategy Expert</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </motion.div>
  );
};

export default SkipperCaseStudy;
